/* eslint-disable max-lines */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, ChevronDown, Home } from 'lucide-react';
import { useDebouncedValue } from '@utils/hooks/use-debounced-value';
import { useOrganization } from '@utils/api/users/use-get-organization';
import { TokenExtension } from '@utils/extensions/token.extension';
import { useSwitchOrganization } from '@utils/api/back-office/use-switch-organization';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { getHomeOrg, saveHomeOrg } from '@utils/extensions/home-org.extension';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal';

// ─── Generic OrgSwitcher ─────────────────────────────────────────────────────
//
// The visual shell — dropdown + search + back-to-home — that both the SYSOP
// back-office switcher and the reseller switcher reuse. Each surface supplies
// its own data hook, switch endpoint, and home-org localStorage prefix.

export interface OrgOption {
  id: number | string;
  label: string;
  value: number | string;
  email?: string | null;
  isActive: boolean;
  // When false the org is shown but cannot be switched into (e.g. a reseller
  // sub-customer that hasn't granted full transcript access — the backend
  // returns 403). Defaults to switchable when omitted so the SYSOP switcher,
  // which never sets this, is unaffected.
  switchable?: boolean;
}

export interface UseOrgsDropdownResult {
  options: OrgOption[];
  isLoading: boolean;
  isFetching: boolean;
}

export interface OrgSwitcherProps {
  // localStorage namespace for the user's home org. Different surfaces use
  // different prefixes ('sysop', 'reseller') so they don't collide.
  homeOrgKeyPrefix: string;
  // POST URL the switch hook should call.
  switchEndpoint: string;
  // Callback invoked with the debounced query; returns the option list +
  // loading state. The dropdown owns the input, this hook owns the fetch.
  useOptions: (q: string, enabled: boolean) => UseOrgsDropdownResult;
  // Where to land after switching back to the home org. Defaults to '/'.
  // Set to '/back-office/customers' for SYSOP, '/reseller' for resellers.
  homeRedirectTo?: string;
  // 'up' opens the dropdown above the trigger (use when the switcher sits at
  // the bottom of a sidebar). Defaults to 'down'.
  dropdownPlacement?: 'up' | 'down';
  // i18n translation keys with sensible defaults.
  i18n?: {
    placeholderKey?: string;
    backToHomeKey?: string;
    confirmTitleKey?: string;
    confirmSubtitleKey?: string;
    switchActionKey?: string;
    cancelKey?: string;
    noResultsKey?: string;
    inactiveBadgeKey?: string;
    notSwitchableBadgeKey?: string;
  };
}

const DEFAULT_KEYS = {
  placeholderKey: 'switch-org-placeholder',
  backToHomeKey: 'back-to-my-org',
  confirmTitleKey: 'switch-org-confirm-title',
  confirmSubtitleKey: 'switch-org-confirm-subtitle',
  switchActionKey: 'switch-action',
  cancelKey: 'cancel',
  noResultsKey: 'no-results',
  inactiveBadgeKey: 'switch-org-inactive',
  notSwitchableBadgeKey: 'switch-org-no-access'
};

export function OrgSwitcher({
  homeOrgKeyPrefix,
  switchEndpoint,
  useOptions,
  homeRedirectTo,
  dropdownPlacement = 'down',
  i18n
}: OrgSwitcherProps) {
  const keys = { ...DEFAULT_KEYS, ...i18n };
  const { t } = useTranslation();
  const { data: organization } = useOrganization();
  const { mutate: switchOrg, isLoading: isSwitching } = useSwitchOrganization({
    endpoint: switchEndpoint
  });
  const { mutate: switchOrgToHome } = useSwitchOrganization({
    endpoint: switchEndpoint,
    redirectTo: homeRedirectTo ?? '/'
  });
  const { getColour, theme } = useThemeContext();

  // Local confirmation state — we render our own ConfirmationModal instead of
  // routing through ConfirmProvider so the title/caption stay correct (the
  // shared Confirm intentionally hardcodes "unsaved changes" copy).
  const [pendingSwitch, setPendingSwitch] = useState<{
    orgId: string;
    orgName: string;
  } | null>(null);

  const token = TokenExtension.get();
  const uid = token?.decoded?.uid;
  const currentOid = token?.decoded?.oid?.toString() ?? '';

  useEffect(() => {
    if (!uid || !currentOid || !organization?.name) return;
    if (!getHomeOrg(homeOrgKeyPrefix, uid)) {
      saveHomeOrg(homeOrgKeyPrefix, uid, currentOid, organization.name);
    }
  }, [uid, currentOid, organization?.name, homeOrgKeyPrefix]);

  const homeOrg = uid ? getHomeOrg(homeOrgKeyPrefix, uid) : null;
  const isOnHomeOrg = !homeOrg || homeOrg.orgId === currentOid;
  const currentOrgName = organization?.name ?? '';

  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const debouncedQuery = useDebouncedValue(inputValue, 300);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    options: orgOptions,
    isLoading: isLoadingOrgs,
    isFetching: isFetchingOrgs
  } = useOptions(debouncedQuery, isOpen);

  const openDropdown = useCallback(() => {
    setIsOpen(true);
    setInputValue('');
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setInputValue('');
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        closeDropdown();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, closeDropdown]);

  const doSwitch = useCallback(
    (orgId: string, orgName: string) => {
      closeDropdown();
      setPendingSwitch({ orgId, orgName });
    },
    [closeDropdown]
  );

  const confirmPendingSwitch = useCallback(() => {
    if (!pendingSwitch) return;
    const { orgId, orgName } = pendingSwitch;
    setPendingSwitch(null);
    switchOrg({ organizationId: orgId, orgName });
  }, [pendingSwitch, switchOrg]);

  const cancelPendingSwitch = useCallback(() => {
    setPendingSwitch(null);
  }, []);

  const handleSelectOrg = useCallback(
    (option: OrgOption) => {
      // Not switchable (e.g. no full transcript access) — the row is shown for
      // context but selecting it is a no-op; the backend would 403 anyway.
      if (option.switchable === false) return;
      if (String(option.value) === currentOid) {
        closeDropdown();
        return;
      }
      doSwitch(String(option.value), option.label);
    },
    [currentOid, doSwitch, closeDropdown]
  );

  const handleBackToHome = useCallback(() => {
    if (!homeOrg) return;
    // No confirmation for return-home — the dedicated home button is already
    // an explicit gesture, a second prompt is just friction.
    switchOrgToHome({
      organizationId: homeOrg.orgId,
      orgName: homeOrg.orgName
    });
  }, [homeOrg, switchOrgToHome]);

  /* eslint-disable no-restricted-syntax -- Semantic accent used across switcher variants */
  const accentColor = '#5794f6';
  /* eslint-enable no-restricted-syntax */
  const borderColor = isOpen ? accentColor : getColour('border');
  const textColor = getColour('normalText');
  const menuBg = getColour('secondLayer');
  const hoverBg =
    theme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';

  return (
    <div
      ref={containerRef}
      className="hidden lg:flex w-full min-w-0 items-center gap-1.5"
    >
      <div className="relative flex-1 min-w-0">
        <div
          className="flex items-center h-8 rounded-2xl border px-2 gap-1.5 cursor-pointer transition-colors"
          style={{ borderColor, backgroundColor: 'transparent' }}
          onClick={() => !isOpen && openDropdown()}
        >
          <Building2
            className="h-3.5 w-3.5 shrink-0"
            style={{ color: accentColor }}
            aria-hidden="true"
          />
          {isOpen ? (
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Escape') closeDropdown();
              }}
              placeholder={currentOrgName || t(keys.placeholderKey)}
              className="flex-1 min-w-0 bg-transparent outline-none text-xs"
              style={{ color: textColor }}
            />
          ) : (
            <span
              className="flex-1 min-w-0 truncate text-xs select-none"
              style={{ color: textColor }}
              title={currentOrgName}
            >
              {currentOrgName || t(keys.placeholderKey)}
            </span>
          )}
          <ChevronDown
            className="h-3 w-3 shrink-0 opacity-40"
            style={{ color: textColor }}
            aria-hidden="true"
          />
        </div>

        {isOpen && (
          <div
            className={`absolute left-0 z-50 w-72 rounded-xl border shadow-xl overflow-hidden ${dropdownPlacement === 'up' ? 'bottom-full mb-1' : 'top-full mt-1'}`}
            style={{
              backgroundColor: menuBg,
              borderColor: getColour('border')
            }}
          >
            <div className="max-h-72 overflow-y-auto py-1">
              {isLoadingOrgs || (isFetchingOrgs && orgOptions.length === 0) ? (
                <p
                  className="px-3 py-3 text-xs text-center opacity-50"
                  style={{ color: textColor }}
                >
                  {'...'}
                </p>
              ) : orgOptions.length === 0 ? (
                <p
                  className="px-3 py-3 text-xs text-center opacity-50"
                  style={{ color: textColor }}
                >
                  {t(keys.noResultsKey)}
                </p>
              ) : (
                orgOptions.map(option => {
                  const notSwitchable = option.switchable === false;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleSelectOrg(option)}
                      disabled={notSwitchable}
                      title={
                        notSwitchable
                          ? t(keys.notSwitchableBadgeKey)
                          : undefined
                      }
                      className="w-full text-left px-3 py-2 flex items-start justify-between gap-2 transition-colors"
                      style={{
                        backgroundColor:
                          String(option.value) === currentOid
                            ? `${accentColor}22`
                            : undefined,
                        color: textColor,
                        cursor: notSwitchable ? 'not-allowed' : undefined,
                        opacity: notSwitchable ? 0.5 : undefined
                      }}
                      onMouseEnter={e => {
                        if (notSwitchable) return;
                        if (String(option.value) !== currentOid)
                          (
                            e.currentTarget as HTMLElement
                          ).style.backgroundColor = hoverBg;
                      }}
                      onMouseLeave={e => {
                        if (notSwitchable) return;
                        if (String(option.value) !== currentOid)
                          (
                            e.currentTarget as HTMLElement
                          ).style.backgroundColor = '';
                      }}
                    >
                      <div className="flex flex-col min-w-0 flex-1">
                        <span className="text-sm font-medium truncate">
                          <span className="opacity-40 text-xs font-mono font-normal">
                            #{option.id}{' '}
                          </span>
                          {option.label}
                        </span>
                        {option.email && (
                          <span
                            className="text-xs opacity-50 truncate"
                            style={{ color: textColor }}
                          >
                            {option.email}
                          </span>
                        )}
                      </div>
                      {!option.isActive ? (
                        <span className="shrink-0 self-start mt-0.5 inline-flex items-center rounded-2xl px-1.5 py-0.5 text-xs font-medium bg-red-100 text-red-700">
                          {t(keys.inactiveBadgeKey)}
                        </span>
                      ) : (
                        notSwitchable && (
                          <span className="shrink-0 self-start mt-0.5 inline-flex items-center rounded-2xl px-1.5 py-0.5 text-xs font-medium bg-amber-100 text-amber-700">
                            {t(keys.notSwitchableBadgeKey)}
                          </span>
                        )
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {!isOnHomeOrg && (
        <button
          type="button"
          onClick={handleBackToHome}
          disabled={isSwitching}
          title={
            homeOrg?.orgName
              ? `${t(keys.backToHomeKey)}: ${homeOrg.orgName}`
              : t(keys.backToHomeKey)
          }
          aria-label={t(keys.backToHomeKey)}
          className="shrink-0 h-8 w-8 rounded-2xl flex items-center justify-center transition-colors hover:opacity-80 disabled:opacity-50"
          style={{ backgroundColor: `${accentColor}22`, color: accentColor }}
        >
          <Home className="h-3.5 w-3.5" />
        </button>
      )}

      <ConfirmationModal
        isOpen={!!pendingSwitch}
        onClose={cancelPendingSwitch}
        onConfirm={confirmPendingSwitch}
        title={
          pendingSwitch
            ? t(keys.confirmTitleKey, { orgName: pendingSwitch.orgName })
            : ''
        }
        caption={t(keys.confirmSubtitleKey)}
        confirmLabel={t(keys.switchActionKey)}
        cancelLabel={t(keys.cancelKey)}
        destructive={false}
        autoFocusConfirm
      />
    </div>
  );
}
